import {Source, Layer} from '../MaptilerWrapper'
import {useMemo} from 'react'
import PropTypes from 'prop-types'
import {useConfig} from '../../../../../store/hooks/config'
import {formatPercentage, generateColorStops, getMapColors, getPaintProperty} from '../helpers'
import {useTheme} from '@mui/material/styles'
import {CircularProgress} from '@mui/material'
import {StyledCensusTractBox} from '../Tooltips/Styles'
import {useAppSelector} from '../../../../../store/hooks'
import {selectSelectedGradientView, selectSelectedPopulationViewMode} from '../../../../../store/slices/focusLayerSlice'

// All Tileset
const TILESET_SOURCES = [
  {
    id: 'censustract',
    url: 'https://api.maptiler.com/tiles/019bfe3f-d980-7be8-a451-ff99e282ebbe/tiles.json',
    minzoom: 0,
    maxzoom: 7,
    fillMinzoom: 0,
    fillMaxzoom: 6
  },
  {
    id: 'censustract1',
    url: 'https://api.maptiler.com/tiles/019c7669-efbe-7066-bd45-1ce17ff6d2ec/tiles.json',
    minzoom: 6,
    maxzoom: 11,
    fillMinzoom: 5,
    fillMaxzoom: 11
  },
  {
    id: 'censustract2',
    url: 'https://api.maptiler.com/tiles/019c0465-912e-7702-8b73-4fb5c6c2a213/tiles.json',
    minzoom: 11,
    maxzoom: 14,
    fillMinzoom: 9,
    fillMaxzoom: 14
  },
  {
    id: 'censustract3',
    url: 'https://api.maptiler.com/tiles/019c79d6-89ba-76b9-ac89-d6adc239295f/tiles.json',
    minzoom: 14,
    maxzoom: 15,
    fillMinzoom: 12,
    fillMaxzoom: 15
  },
  {
    id: 'censustract4',
    url: 'https://api.maptiler.com/tiles/019c4650-9ea5-7cac-a984-a83d75f5184a/tiles.json',
    minzoom: 15,
    maxzoom: 16,
    fillMinzoom: 13,
    fillMaxzoom: 16
  }
]

const LABEL_SOURCE_URL = 'https://api.maptiler.com/tiles/019c7a7c-d486-7abc-aee7-977cbf52dca7/tiles.json'

const CensusTractLayer = ({
  censusTractData,
  censusTractTilesetSourceLayer,
  visibility,
  censusTractFipsToPersona,
  personasToggle
}) => {
  const theme = useTheme()

  const mapColors = useMemo(() => getMapColors(theme), [theme])
  const {personasConfig} = useConfig()
  const selectedGradientView = useAppSelector(selectSelectedGradientView)
  const selectedPopulationViewMode = useAppSelector(selectSelectedPopulationViewMode)

  // Deduplicated census tract records — forward iteration, last occurrence wins
  const filteredData = useMemo(() => {
    if (!censusTractData?.censusTract?.censusTract) return null
    const lastOccurrenceMap = new Map()
    for (const ct of censusTractData.censusTract.censusTract) {
      lastOccurrenceMap.set(ct.censusTractGeoId, ct)
    }
    return Array.from(lastOccurrenceMap.values())
  }, [censusTractData])

  // Counts aggregation
  const populationData = useMemo(() => {
    if (!filteredData) return null
    return [
      ...filteredData.map((ct) => [ct.censusTractGeoId, ct.count]),
      ...(censusTractData?.censusTract?.missingGeoIds ?? []).map((fips) => [fips, 0])
    ]
  }, [filteredData, censusTractData])

  // Mode 1 — selected CT / total county population (parent geography)
  const aggForCountyTotal = useMemo(() => {
    if (!filteredData) return null
    return [
      ...filteredData
        .filter((ct) => ct.countyPercentage != null)
        .map((ct) => [ct.censusTractGeoId, formatPercentage(ct.countyPercentage)]),
      ...(censusTractData?.censusTract?.missingGeoIds ?? []).map((fips) => [fips, 0])
    ]
  }, [filteredData, censusTractData])

  // Mode 2 — selected CT / total census tract population (self geography)
  const aggForCensusTract = useMemo(() => {
    if (!filteredData) return null
    return [
      ...filteredData
        .filter((ct) => ct.censusTractPercentage != null)
        .map((ct) => [ct.censusTractGeoId, formatPercentage(ct.censusTractPercentage)]),
      ...(censusTractData?.censusTract?.missingGeoIds ?? []).map((fips) => [fips, 0])
    ]
  }, [filteredData, censusTractData])

  // Colors for counts
  const colors = useMemo(() => {
    if (!populationData) return null
    return generateColorStops(populationData, mapColors, false)
  }, [populationData, mapColors])

  const colorsForCountyTotal = useMemo(() => {
    if (!aggForCountyTotal?.length) return null
    return generateColorStops(aggForCountyTotal, mapColors, true)
  }, [aggForCountyTotal, mapColors])

  const colorsForCensusTract = useMemo(() => {
    if (!aggForCensusTract?.length) return null
    return generateColorStops(aggForCensusTract, mapColors, true)
  }, [aggForCensusTract, mapColors])

  const layerType = personasToggle ? 'personas' : 'censusTract'

  const colorsMap = (() => {
    if (selectedGradientView === 'population') return colors
    if (selectedPopulationViewMode === 'selected_of_county') return colorsForCountyTotal
    return colorsForCensusTract
  })()

  const aggregationMap = (() => {
    if (selectedGradientView === 'population') return populationData
    if (selectedPopulationViewMode === 'selected_of_county') return aggForCountyTotal
    return aggForCensusTract
  })()

  const getWidth = (zoom) => {
    if (zoom >= 14) return 1.5
    if (zoom >= 11) return 1
    if (zoom >= 6) return 0.7
    return 0.3
  }

  const getOpacity = (zoom) => {
    if (zoom >= 14) return 1
    if (zoom >= 11) return 1
    if (zoom >= 6) return 0.9
    return 0.5
  }
  // Show loader when populationData is not yet available
  if (!populationData || !colors) {
    return (
      <StyledCensusTractBox>
        <CircularProgress size={20} />
      </StyledCensusTractBox>
    )
  }
  return (
    populationData &&
    colors && (
      <>
        {TILESET_SOURCES.map(({id, url, minzoom, maxzoom, fillMinzoom}) => {
          const suffix = id.replace('censustract', '') // Extract suffix from id

          return (
            <Source id={id} type='vector' url={url} promoteId='GEOID' key={id}>
              {aggregationMap && colorsMap && (
                <Layer
                  beforeId='state-border'
                  id={'censustract-fill' + suffix}
                  type='fill'
                  source={id}
                  source-layer={censusTractTilesetSourceLayer}
                  filter={['in', ['get', 'GEOID'], ['literal', aggregationMap.map((d) => d[0])]]}
                  paint={getPaintProperty(
                    layerType,
                    censusTractFipsToPersona,
                    personasConfig,
                    aggregationMap,
                    colorsMap
                  )}
                  layout={{visibility}}
                  data-testid={'censustract-fill' + suffix}
                  minzoom={fillMinzoom}
                  maxzoom={maxzoom}
                />
              )}
              <Layer
                beforeId='state-border'
                id={'censustract-border' + suffix}
                type='line'
                source={id}
                source-layer={censusTractTilesetSourceLayer}
                paint={{
                  'line-color': theme.palette.hpWhite,
                  'line-width': getWidth(minzoom), // Adjust line width based on zoom level
                  'line-opacity': getOpacity(minzoom)
                }}
                minzoom={minzoom}
                maxzoom={maxzoom}
              />
              <Layer
                id={'censustract-border-onhover' + suffix}
                type='line'
                source={id}
                source-layer={censusTractTilesetSourceLayer}
                paint={{
                  'line-color': theme.palette.hpAccentBlack,
                  'line-width': 2,
                  'line-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 1, 0]
                }}
                minzoom={minzoom}
                maxzoom={maxzoom}
              />
              <Layer
                id={'censustract-border-onhover-stroke' + suffix}
                type='line'
                source={id}
                source-layer={censusTractTilesetSourceLayer}
                paint={{
                  'line-color': theme.palette.hpWhite,
                  'line-width': minzoom < 10 ? 1 : 2,
                  'line-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 1, 0],
                  'line-gap-width': minzoom < 10 ? 1 : 2
                }}
                minzoom={minzoom}
                maxzoom={maxzoom}
              />
            </Source>
          )
        })}

        {/* Label layer below */}
        <Source id='censustract-label' type='vector' url={LABEL_SOURCE_URL} promoteId='GEOID'>
          {/* Below layer will display census tract labels through the tiles via maptiler cloud when choropleth is on */}
          <Layer
            beforeId='state-border'
            id='censustract-label'
            type='symbol'
            source='censustract-label'
            minzoom={13}
            source-layer={censusTractTilesetSourceLayer}
            filter={['in', ['get', 'GEOID'], ['literal', populationData ? populationData.map((d) => d[0]) : []]]}
            layout={{
              'text-field': ['format', ['get', 'GEOID']],
              'text-font': ['Noto Sans Extra Bold'],
              'text-padding': 1,
              'text-size': {
                base: 2,
                stops: [
                  [2, 8],
                  [8, 15]
                ]
              },
              'text-max-width': 4,
              'text-offset': [0, 0],
              'text-transform': 'uppercase',
              'text-anchor': 'center'
            }}
            paint={{
              'text-color': theme.palette.hpAccentBlack,
              'text-halo-color': theme.palette.hpWhite,
              'text-halo-width': 1
            }}
          />
        </Source>
      </>
    )
  )
}

CensusTractLayer.propTypes = {
  censusTractData: PropTypes.object,
  censusTractTilesetSourceLayer: PropTypes.string,
  visibility: PropTypes.string,
  censusTractFipsToPersona: PropTypes.object,
  personasToggle: PropTypes.bool
}

export default CensusTractLayer
